import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { pickBy, noop, omit } from 'lodash';
import Text from '@dbk/ui-text';

export const BODY_TYPE = 'body-text';
const titleTextClassName = 'accordion__item-title--text';
const bodyTextClassName = 'accordion__content--text';

const isElement = (component) => React.isValidElement(component);

const cloneElementAndTextWithClass = (component, other, type) => {
  if (component.type === Text) {
    return React.cloneElement(component, {
      ...other,
      className: classnames([
        component.props.className,
        type !== BODY_TYPE && titleTextClassName,
        type === BODY_TYPE && bodyTextClassName,
      ]),
    });
  }

  return React.cloneElement(component, {
    ...other,
  });
};

export const renderComponent = (children, numOfLines, params = [], type) => {
  if (typeof children === 'function') {
    return children(params.style, params.itemKey);
  }
  const filteredParams = omit(params, ['itemKey']);
  return isElement(children) ? (
    cloneElementAndTextWithClass(children, filteredParams, type)
  ) : (
    <Text
      {...filteredParams}
      className={classnames([
        type !== BODY_TYPE && titleTextClassName,
        type === BODY_TYPE && bodyTextClassName,
      ])}
      numberOfLines={numOfLines}
    >
      {children}
    </Text>
  );
};

const isItemActive = (currentItems, itemKey) =>
  currentItems.indexOf(itemKey) > -1;

const getActiveItemsFromChildProps = (accordionItems, multiSelect) => {
  const activeItems = React.Children.toArray(accordionItems)
    .filter((accordionItem) => React.isValidElement(accordionItem))
    .filter((accordionItem) => accordionItem.props.active)
    .map((accordionItem) => accordionItem.props.itemKey);

  if (!multiSelect && activeItems.length) {
    return [activeItems[0]];
  }

  return activeItems;
};

const getNewActiveItems = (itemKey, currentItems, multiSelect = false) => {
  const isCurrentItemActive = isItemActive(currentItems, itemKey);
  if (!multiSelect) {
    return isCurrentItemActive ? [] : [itemKey];
  }

  if (isCurrentItemActive) {
    return currentItems.filter((key) => key !== itemKey);
  }

  return currentItems.concat([itemKey]);
};

const getAccordionItems = (
  { activeItems },
  {
    renderIconForOpen,
    renderIconForClose,
    children: accordionItems,
    fullWidth,
    iconPosition,
  },
  handleChange
) => {
  const accordionIcons = {
    renderIconForOpen,
    renderIconForClose,
  };

  return React.Children.map(accordionItems, (accordionItem) => {
    if (!React.isValidElement(accordionItem)) {
      return null;
    }

    const itemIcons = pickBy(
      accordionItem.props,
      (value, name) =>
        ['renderIconForOpen', 'renderIconForClose'].includes(name) &&
        value !== undefined
    );

    const icons = { ...accordionIcons, ...itemIcons };
    const active = isItemActive(activeItems, accordionItem.props.itemKey);
    return React.cloneElement(accordionItem, {
      handleChange,
      active,
      fullWidth,
      iconPosition,
      ...icons,
    });
  });
};

const iconProps = {
  iconPosition: PropTypes.oneOf(['left', 'right']),
};

const iconDefaultProps = {
  iconPosition: 'right',
};

const commonDefaultProps = {
  className: '',
  fullWidth: false,
  style: {},
  ...iconDefaultProps,
};

const commonPropTypes = {
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  style: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  ...iconProps,
};

const renderPropChild = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.object,
  PropTypes.element,
  PropTypes.string,
]);

/* Accordion props */
export const accordionDefaultProps = {
  activeItems: [],
  children: [],
  onChange: noop,
  renderIconForOpen: noop,
  renderIconForClose: noop,
  allowMultiple: false,
  ...commonDefaultProps,
};

export const accordionPropTypes = {
  activeItems: PropTypes.array,
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.element,
  ]),
  onChange: PropTypes.func,
  renderIconForOpen: renderPropChild,
  renderIconForClose: renderPropChild,
  allowMultiple: PropTypes.bool,
  ...commonPropTypes,
};

export const accordionContextTypes = {
  inverse: PropTypes.bool,
};

/* Accordion Item props */
export const accordionItemDefaultProps = {
  renderTitle: noop,
  handleChange: noop,
  itemKey: Math.random(),
  children: [],
  active: false,
  disabled: false,
  renderIconForOpen: undefined,
  renderIconForClose: undefined,
  ...commonDefaultProps,
};

export const accordionItemPropTypes = {
  renderTitle: renderPropChild,
  handleChange: PropTypes.func,
  itemKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
    PropTypes.func,
  ]),
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  renderIconForOpen: renderPropChild,
  renderIconForClose: renderPropChild,
  ...commonPropTypes,
};

/* Accordion Title props */
export const accordionTitleDefaultProps = {
  onClick: noop,
  renderTitle: noop,
  renderIcon: noop,
  isActive: false,
  isDisabled: false,
  fullWidth: false,
  ...iconDefaultProps,
};

export const accordionTitlePropTypes = {
  onClick: PropTypes.func,
  renderTitle: renderPropChild,
  renderIcon: renderPropChild,
  isActive: PropTypes.bool,
  isDisabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  ...iconProps,
};

/* Accordion Body props */
export const accordionBodyDefaultProps = {
  children: [],
  isActive: false,
  isDisabled: false,
  fullWidth: false,
};

export const accordionBodyPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
    PropTypes.func,
  ]),
  fullWidth: PropTypes.bool,
};

export { getNewActiveItems, getAccordionItems, getActiveItemsFromChildProps };
